import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { createEditTask, getTasks } from '../api/apiTaskManager';
import './Dashboard.scss';
// Material UI
import { Box, Tab, Tabs, useMediaQuery } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress'; // Import MUI CircularProgress
import { useTheme } from '@mui/material/styles';
// Components
import EmployeeStatus from '../components/home/employeestatus/EmployeeStatus';
import EmployeeStatus_noSticky from '../components/home/employeestatus/EmployeeStatus_noSticky';
import ServicesGrid from '../components/home/serviceslist/ServicesGrid';
import TaskInput from '../components/home/taskinput/TaskInput';
import TaskManager from '../components/home/taskmanager/TaskManager';
import TaskManager_AdminMenu from '../components/home/taskmanager/TaskManager_AdminMenu';
import TasksListAuthorize from '../components/home/taskslist/TasksListAuthorize';
import TasksListMain from '../components/home/taskslist/TasksListMain';
import TasksListSpecial from '../components/home/taskslist/TasksListSpecial';
import useResetTaskHistory from '../hook/useResetTaskHistory'; // Hook to reset task history
import { useSettings } from '../hook/useSettings'; // Hook to fetch settings
import GridLayout from '../ui/GridLayout';
// Import Supabase client
import supabase from '../api/supabase';
import useCurrentUser from '../components/user/useCurrentUser'; // Hook get current login user data

function Dashboard() {
  // TODO Material UI
  const [activeTab, setActiveTab] = useState(0); // State to track active tab
  const [userRole, setUserRole] = useState([]); // Store user roles

  // Define the allowed roles that can see the "MANAGER" tab
  const allowedRoles = ['Manager', 'Owner'];

  // Fetch the current user's roles using the hook
  const { currentUser, loading } = useCurrentUser();
  const theme = useTheme();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md')); // Detect mobile and tablet screens (below 900px)

  // Use custom hook to fetch settings
  const { settingsData, loading: loadingSettings, errorSettings } = useSettings();

  // Use custom hook to save data to taskManager_history
  const { resetTaskHistory, isProcessing: isProcessingReset } = useResetTaskHistory();

  // Handle grid layout based on themeGrid value
  const themeGrid = settingsData?.themeGrid;

  // Set user roles when the current user data is available
  useEffect(() => {
    if (currentUser && currentUser.empRole) {
      setUserRole(currentUser.empRole); // Set user roles from the current user
    }
  }, [currentUser]);

  // Check if the user role is allowed to see the "MANAGER" tab
  const isManagerTabDisabled = !userRole.some(role => allowedRoles.includes(role));

  // Set the active tab
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // TODO State variables
  const [taskRecord, setTaskRecord] = useState([]); // Moved this useState call to the top
  const [activeCell, setActiveCell] = useState(null); // Active cell state
  const [taskInput, setTaskInput] = useState(null);
  const [activeName, setActiveName] = useState(null);

  // Setup react-query's queryClient for cache management
  const queryClient = useQueryClient();

  // Fetch taskManager from the database
  const {
    isLoading: isLoadingTasks,
    data: tasksData,
    error: errorTasks,
  } = useQuery({
    queryKey: ['taskManager'],
    queryFn: getTasks,
  });

  // Initialize taskRecord with fetched tasksData
  useEffect(() => {
    if (tasksData) {
      setTaskRecord(tasksData);
    }
  }, [tasksData]);

  // TODO Add Supabase real-time subscription for taskManager table
  useEffect(() => {
    const channel = supabase
      .channel('taskManager:realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'taskManager' }, () => {
        queryClient.invalidateQueries({ queryKey: ['taskManager'] });
      })
      .subscribe();

    // Cleanup the subscription on component unmount
    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  // Mutation to update or create a task in the taskManager database
  const { mutate: updateTask } = useMutation({
    mutationFn: createEditTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taskManager'] });
    },
    onError: error => {
      console.error('Error creating or updating task:', error);
    },
  });

  // Create a promise wrapper for task update with toast.promise
  const updateTaskPromise = finalRecord => {
    return new Promise((resolve, reject) => {
      updateTask(finalRecord, {
        onSuccess: () => resolve(),
        onError: error => reject(error),
      });
    });
  };

  // Handle loading and error states
  if (isLoadingTasks || loadingSettings || loading || isProcessingReset) {
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
      }}
    >
      <CircularProgress /> {/* MUI Loading Indicator */}
    </div>;
  }

  if (errorTasks || errorSettings) {
    toast.error(errorTasks.message);
    return <div>Error loading tasks</div>;
  }

  // TODO Handle click events
  const handleCellClick = (recordIndex, taskKey, selectedTask) => {
    setActiveCell({ recordIndex, taskKey });
    setActiveName(taskRecord[recordIndex].name);
    setTaskInput({
      ...selectedTask,
      avatar: taskRecord[recordIndex].avatar,
      name: taskRecord[recordIndex].name,
      column: taskKey,
    });
  };

  // Update service click with toast.promise
  const handleServiceClick = selectedService => {
    if (activeCell) {
      const updatedRecords = taskRecord.map((record, index) => {
        if (index === activeCell.recordIndex) {
          const updatedTask = {
            ...record[activeCell?.taskKey],
            ...selectedService,
          };

          const updatedRecord = {
            ...record,
            [activeCell.taskKey]: updatedTask,
          };

          // Calculate new totals for credits, income, and supplies
          const newCredits = [...Array(20).keys()].reduce((total, i) => {
            return total + (updatedRecord[`t${i + 1}`].serCredit || 0);
          }, 0);

          const newIncome = [...Array(20).keys()].reduce((total, i) => {
            return total + (updatedRecord[`t${i + 1}`].serAmount || 0);
          }, 0);

          const newSupplies = [...Array(20).keys()].reduce((total, i) => {
            return total + (updatedRecord[`t${i + 1}`]?.serSupply || 0);
          }, 0);

          const finalRecord = {
            ...updatedRecord,
            credits: newCredits,
            income: newIncome,
            supplies: newSupplies,
            id: record.id, // Ensure 'id' is included
          };

          // Use toast.promise to handle success and error notifications
          toast.promise(updateTaskPromise(finalRecord), {
            loading: 'Saving...',
            success: 'Task saved!',
            error: 'Could not save.',
          });

          return finalRecord;
        }
        return record;
      });

      setTaskRecord(updatedRecords);
      setTaskInput({
        ...selectedService,
        avatar: taskRecord[activeCell.recordIndex].avatar,
        name: taskRecord[activeCell.recordIndex].name,
        column: activeCell.taskKey,
      });

      // Corrected: Call the hook to save records to taskManager_history table
      resetTaskHistory(); // Add parentheses to call the function
    }
  };

  // Update save with toast.promise
  const handleSave = updatedTask => {
    if (activeCell) {
      const updatedRecords = taskRecord.map((record, index) => {
        if (index === activeCell.recordIndex) {
          const updatedRecord = {
            ...record,
            [activeCell.taskKey]: {
              ...record[activeCell.taskKey],
              ...updatedTask,
            },
          };

          // Recalculate the total income and supplies for the row
          const newIncome = [...Array(20).keys()].reduce((total, i) => {
            return total + (updatedRecord[`t${i + 1}`]?.serAmount || 0);
          }, 0);

          const newSupplies = [...Array(20).keys()].reduce((total, i) => {
            return total + (updatedRecord[`t${i + 1}`]?.serSupply || 0);
          }, 0);

          const finalRecord = {
            ...updatedRecord,
            income: newIncome,
            supplies: newSupplies,
          };

          // Use toast.promise for save operation
          toast.promise(updateTaskPromise(finalRecord), {
            loading: 'Saving...',
            success: 'Task saved!',
            error: 'Could not save.',
          });

          return finalRecord;
        }
        return record;
      });

      setTaskRecord(updatedRecords);

      // Corrected: Call the hook to save records to taskManager_history table
      resetTaskHistory(); // Add parentheses to call the function
    }
  };

  const handleNameClick = name => {
    setActiveName(name);
    const index = taskRecord.findIndex(record => record.name === name);
    setActiveCell({ recordIndex: index });
  };

  const Grid1Content = (
    <div className="dashboardContainer">
      <TaskManager
        taskRecord={taskRecord}
        onCellClick={handleCellClick}
        activeCell={activeCell}
        activeName={activeName}
      />
      {isSmallScreen && !themeGrid && (
        <TaskManager_AdminMenu
          tab1={
            <>
              <EmployeeStatus_noSticky
                taskRecord={taskRecord} // Pass task records
                activeCell={activeCell} // Pass active cell state
                setTaskRecord={setTaskRecord} // Pass state update function
              />

              <TasksListMain
                taskRecord={taskRecord}
                activeName={activeName}
                activeCell={activeCell} // Pass active cell state for Employee Status
                setTaskRecord={setTaskRecord} // Pass state update function for Employee Status
                onNameClick={handleNameClick}
              />
            </>
          }
          tab2={
            <div className="flex flex-col gap-4 ">
              <EmployeeStatus_noSticky
                taskRecord={taskRecord} // Pass task records
                activeCell={activeCell} // Pass active cell state
                setTaskRecord={setTaskRecord} // Pass state update function
              />

              <ServicesGrid onServiceClick={handleServiceClick} />
            </div>
          }
          tab3={
            // check empRole for gain accecss
            !isManagerTabDisabled ? (
              <TaskInput taskInput={taskInput} onSave={handleSave} />
            ) : (
              'Limit Access'
            )
          }
          tab4={
            // check empRole for gain accecss
            !isManagerTabDisabled ? (
              <TasksListAuthorize
                taskRecord={taskRecord}
                activeName={activeName}
                onNameClick={handleNameClick}
              />
            ) : (
              'Limit Access'
            )
          }
        />
      )}

      <EmployeeStatus
        taskRecord={taskRecord} // Pass task records
        activeCell={activeCell} // Pass active cell state
        setTaskRecord={setTaskRecord} // Pass state update function
      />
    </div>
  );

  const Grid2Content = (
    <>
      {/* Use Material-UI Tabs */}
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{
          '& .MuiTab-root': {
            textTransform: 'none', // Avoid uppercase labels
            fontWeight: 'bold', // Make labels bold
            color: 'gray', // Default text color
            borderBottom: '1px solid lightgray',
            minWidth: 100, // Minimum width for each tab
            '&.Mui-selected': {
              color: '#ff5722', // Color for selected tab
              borderTopRightRadius: 5,
              borderTopLeftRadius: 5,
              border: '1px solid lightgray', // Border for selected tab
            },
            '&:hover': {
              color: 'black', // Hover state color
            },
            '&.Mui-disabled': {
              color: 'lightgray', // Color for disabled tabs
            },
          },
          '& .MuiTabs-indicator': {
            backgroundColor: '#f3f3f3', // Color of the underline for the active tab
          },
        }}
      >
        <Tab label="ALL" />
        <Tab label="SPECIAL" />
        <Tab label="MANAGER" disabled={isManagerTabDisabled} />
      </Tabs>

      {/* Material-UI TabPanels */}
      {activeTab === 0 && (
        <Box sx={{ paddingY: 1 }}>
          <TasksListMain
            taskRecord={taskRecord}
            activeName={activeName}
            onNameClick={handleNameClick}
          />
        </Box>
      )}
      {activeTab === 1 && (
        <Box sx={{ paddingY: 1 }}>
          <TasksListSpecial
            taskRecord={taskRecord}
            activeName={activeName}
            onNameClick={handleNameClick}
            specialCategory={settingsData.specialCategory}
          />
        </Box>
      )}
      {activeTab === 2 && !isManagerTabDisabled && (
        <Box sx={{ paddingY: 1 }}>
          <TasksListAuthorize
            taskRecord={taskRecord}
            activeName={activeName}
            onNameClick={handleNameClick}
          />
          <TaskInput taskInput={taskInput} onSave={handleSave} />
        </Box>
      )}

      <ServicesGrid onServiceClick={handleServiceClick} />
    </>
  );

  return <GridLayout Grid1={Grid1Content} Grid2={Grid2Content} HideGrid2={true} />;
}

export default Dashboard;
